import { Dispatch, PropsWithChildren, useState } from 'react';
import { LinkId } from '../../common/AnalyticsContext/LinkId';
import { useSearchFilters } from '../../common/components/SearchHeader/filters/useSearchFilters';
import { SPECIALTY_DENTIST } from '../../common/components/SearchHeader/types/FilterChoice';
import { useHannibalQuery } from '../../common/schema/hannibal/HannibalContext';
import {
  FreeTextSearch,
  InsuranceFilterOptionsDocument,
} from '../../common/schema/hannibal/types.generated';
import { useMobile } from '../../common/services/screenSize/ScreenSizeService';
import { DropDownEnumFilter } from '../FilterContainer/EnumFilter/DropDownEnumFilter/DropDownEnumFilter';
import { DropDownFilterPill } from '../FilterContainer/FilterPill/DropDownFilterPill';
import { useVariables } from '../useSearchQuery/useVariables';
import { InfoModal } from './InfoModal/InfoModal';
import {
  CloseIcon,
  ProviderContainer,
  ProviderPillRow,
  ProviderRow,
  Text,
} from './PreferredProviderSection.styles';
import { ProviderPill } from './ProviderPill/ProviderPill';
import { PROVIDERS } from './constants';

type Props = PropsWithChildren<{
  searchFacets?: FreeTextSearch['searchProfiles']['facetsWithCounts'];
  setPreferredProvidersHidden: Dispatch<boolean>;
  preferredProvidersHidden: boolean;
}>;

export const PreferredProviderSection = ({
  searchFacets,
  setPreferredProvidersHidden,
  preferredProvidersHidden,
}: Props) => {
  const isMobile = useMobile();
  const { preferredHealthProviders } = searchFacets ?? {};

  const [selectedProvider, setSelectedProvider] = useState<string | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);

  const [filters, setFilters] = useSearchFilters();

  const { topic } = filters;

  if (
    topic?.specialist !== SPECIALTY_DENTIST ||
    filters.insurance ||
    preferredProvidersHidden ||
    preferredHealthProviders?.count === 0
  ) {
    return null;
  }

  const handleChange = (insurance: string | undefined): void => {
    setSelectedProvider(insurance);
    setModalOpen(true);
  };

  const handleClickFindProvider = (program?: string): void => {
    setModalOpen(false);

    setFilters({
      ...filters,
      insurance: selectedProvider,
      ...(program ? { insuranceProgram: program } : {}),
    });

    setSelectedProvider(undefined);
  };

  const morePill = (
    <DropDownFilterPill
      onClear={() => null}
      hasValue={filters.insurance !== undefined}
      caption="More"
      trackingId={LinkId.PROMINENT_INSURANCE_FILTER_MORE}
      content={<MoreInsurancePillContent handleChange={handleChange} />}
    />
  );

  const desktopView = (
    <>
      {PROVIDERS.map((data, index) => (
        <ProviderPill
          key={index}
          onClick={() => handleChange(data.provider)}
          logo={data.logo}
          trackingId={data.trackingId}
        />
      ))}
      {morePill}
    </>
  );

  const mobileView = (
    <>
      <ProviderRow>
        {PROVIDERS.slice(0, 3).map((data, index) => (
          <ProviderPill
            key={index}
            onClick={() => handleChange(data.provider)}
            logo={data.logo}
            trackingId={data.trackingId}
          />
        ))}
      </ProviderRow>
      <ProviderRow>
        {PROVIDERS.slice(3, 5).map((data, index) => (
          <ProviderPill
            key={index}
            onClick={() => handleChange(data.provider)}
            logo={data.logo}
            trackingId={data.trackingId}
          />
        ))}
        {morePill}
      </ProviderRow>
    </>
  );

  return (
    <>
      <ProviderContainer>
        <ProviderPillRow>
          <CloseIcon onClick={() => setPreferredProvidersHidden(true)} />
          <Text>Select my health fund</Text>
          {isMobile ? mobileView : desktopView}
        </ProviderPillRow>
      </ProviderContainer>
      <InfoModal
        isOpen={modalOpen}
        provider={selectedProvider}
        onClose={() => setModalOpen(false)}
        onClickFindProvider={handleClickFindProvider}
      />
    </>
  );
};

/** The dropdown content lives in its own component so it can defer data-loading until rendered */
const MoreInsurancePillContent = ({
  handleChange,
}: {
  handleChange: (insurance: string | undefined) => void;
}) => {
  const [filters] = useSearchFilters();
  const highlightProviderList = PROVIDERS.map(data => data.provider);

  const queryResult = useHannibalQuery(InsuranceFilterOptionsDocument, {
    variables: useVariables(filters),
  });

  const insuranceOptions =
    queryResult.data?.searchProfiles.facetsWithCounts.preferredHealthProviders.values
      ?.filter(facetResult => !highlightProviderList.includes(facetResult.term))
      .map(facetResult => ({
        label: facetResult.term,
        value: facetResult.term,
      })) || [];

  return (
    <DropDownEnumFilter<string | undefined>
      filterTitle=""
      value={filters.insurance}
      options={insuranceOptions}
      onChange={handleChange}
    />
  );
};
